import React from "react";
import { Table, Typography, Tag, Space, Button, Popconfirm, Empty } from "antd";
import { EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import { ItemModel } from "../model/orders.ui";
import { DesktopPagination } from "./DesktopPagination";
import {
  getOrderNo,
  getCustomerName,
  getOrderDate,
  getDeliveryDate,
  getTotalAmount,
  getStatusConfig,
} from "./orderListHelpers";

const { Text } = Typography;

export interface OrderDesktopTableProps {
  orders: ItemModel[];
  isLoading?: boolean;
  currentPage?: number;
  totalPages?: number;
  isLoadingMore?: boolean;
  onPageChange?: (page: number) => Promise<void>;
  onSelectOrder: (order: ItemModel) => void;
  onDeleteOrder?: (order: ItemModel) => Promise<void> | void;
  deletingId?: number | null;
}

export const OrderDesktopTable: React.FC<OrderDesktopTableProps> = ({
  orders,
  isLoading = false,
  currentPage = 1,
  totalPages = 1,
  isLoadingMore = false,
  onPageChange,
  onSelectOrder,
  onDeleteOrder,
  deletingId,
}) => {
  const columns = [
    {
      title: "注文番号", // Mã đơn hàng
      key: "orderNo",
      render: (_: any, record: any) => (
        <Text strong>{getOrderNo(record)}</Text>
      ),
    },
    {
      title: "顧客・店舗名", // Tên khách hàng / chi nhánh
      key: "customerName",
      render: (_: any, record: any) => getCustomerName(record),
    },
    {
      title: "注文日", // Ngày đặt hàng
      key: "orderDate",
      render: (_: any, record: any) => getOrderDate(record),
    },
    {
      title: "納品希望日", // Ngày giao hàng mong muốn
      key: "deliveryDate",
      render: (_: any, record: any) => getDeliveryDate(record),
    },
    {
      title: "合計金額", // Tổng tiền
      key: "totalAmount",
      render: (_: any, record: any) =>
        `¥${getTotalAmount(record).toLocaleString()}`,
    },
    {
      title: "ステータス", // Trạng thái
      key: "status",
      render: (_: any, record: any) => {
        const config = getStatusConfig(record.status);
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: "操作", // Thao tác
      key: "action",
      render: (_: any, record: ItemModel) => {
        const orderId = record.order_id || record.id;
        return (
          <Space size="small">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => onSelectOrder(record)}
            >
              詳細 {/* Chi tiết */}
            </Button>
            {onDeleteOrder && (
              <Popconfirm
                title="注文の削除" /* Xóa đơn hàng */
                description="この注文を削除してもよろしいですか？" /* Bạn có chắc chắn muốn xóa đơn hàng này? */
                okText="削除" /* Xóa */
                cancelText="キャンセル" /* Hủy */
                okButtonProps={{ danger: true }}
                onConfirm={() => onDeleteOrder(record)}
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingId === orderId}
                >
                  削除 {/* Xóa */}
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        borderRadius: 16,
        height: "100%",
      }}
    >
      <div style={{ flex: 1, overflowY: "auto" }}>
        <Table
          dataSource={orders}
          columns={columns}
          rowKey="id"
          pagination={false}
          sticky
          loading={isLoading}
          scroll={{ x: "max-content" }}
          locale={{ emptyText: <Empty description="該当する注文がありません。" /* Không có đơn hàng phù hợp */ /> }}
        />
      </div>
      {onPageChange && totalPages > 1 && (
        <DesktopPagination
          currentPage={currentPage}
          totalPages={totalPages}
          isLoading={isLoadingMore}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

export const OrderDestopTable = OrderDesktopTable;
export type OrderDestopTableProps = OrderDesktopTableProps;
export default OrderDesktopTable;
